import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();
export const useLanguage = () => useContext(LanguageContext);

const STORAGE_KEY = 'lovgo-user-lang';
const SUPPORTED = ['pt-br', 'en-us', 'es-la'];

const HTML_LANG_MAP = {
  'pt-br': 'pt-BR',
  'en-us': 'en-US',
  'es-la': 'es',
};

const detectFromBrowser = () => {
  const lang = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase();
  if (lang.startsWith('pt')) return 'pt-br';
  if (lang.startsWith('es')) return 'es-la';
  return 'en-us';
};

const detectFromTimezone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/America\/(Sao_Paulo|Fortaleza|Manaus|Belem|Recife|Maceio|Cuiaba|Porto_Velho|Boa_Vista|Noronha|Araguaina)/.test(tz)) return 'pt-br';
    if (/Europe\/(Lisbon)/.test(tz)) return 'pt-br';
    if (/America\/(Mexico_City|Bogota|Lima|Santiago|Buenos_Aires|Caracas|Montevideo|Asuncion|La_Paz|Guayaquil|Managua|Tegucigalpa|San_Jose|Guatemala|El_Salvador|Santo_Domingo|Panama|Havana)/.test(tz)) return 'es-la';
    if (/Europe\/(Madrid)/.test(tz)) return 'es-la';
  } catch (error) {
    console.warn('Timezone detection failed:', error);
  }
  return null;
};

export const LanguageProvider = ({ children }) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  const initial = saved && SUPPORTED.includes(saved) ? saved : detectFromBrowser();

  const [language, setLanguage] = useState(initial);
  const [isAutoDetected, setIsAutoDetected] = useState(!!saved);

  // Sync <html lang=""> for SEO and accessibility
  useEffect(() => {
    document.documentElement.lang = HTML_LANG_MAP[language] || 'en';
  }, [language]);

  useEffect(() => {
    if (saved) return;

    const tzLang = detectFromTimezone();
    if (tzLang) {
      setLanguage(tzLang);
      setIsAutoDetected(true);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        clearTimeout(timeout);
        const cc = data?.country_code || '';
        if (['BR', 'PT'].includes(cc)) setLanguage('pt-br');
        else if (['ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'BO', 'PY', 'UY', 'CR', 'PA', 'DO', 'GT', 'HN', 'SV', 'NI', 'CU'].includes(cc)) setLanguage('es-la');
        else setLanguage(detectFromBrowser());
      })
      .catch((error) => {
        clearTimeout(timeout);
        console.warn('IP geolocation failed, using fallback.', error);
      })
      .finally(() => setIsAutoDetected(true));

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [saved]);

  const toggleLanguage = (lang) => {
    if (!SUPPORTED.includes(lang)) return;
    setLanguage(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, isAutoDetected }}>
      {children}
    </LanguageContext.Provider>
  );
};